import { useMemo, useState } from "react";
import { Link } from "react-router";
import { useGetIdentity, useGetList, useStore, useTranslate } from "ra-core";

import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { useIsMobile } from "@/hooks/use-mobile";
import { MobileContent } from "@/components/atomic-crm/layout/MobileContent";
import MobileHeader from "@/components/atomic-crm/layout/MobileHeader";
import { TaskAreasDialog } from "./TaskAreasDialog";
import { TaskTemplatesDialog } from "./TaskTemplatesDialog";
import { TaskQuickCreate } from "./TaskQuickCreate";
import { TaskGroup } from "./TaskRow";
import { groupOpenTasks, type TenaxTask } from "./taskGroups";
import {
  OPEN_STATUSES,
  fullName,
  nativeSelectClass,
  type ChecklistItem,
  useTaskAreas,
  useTeam,
} from "./taskData";

type StatusFilter = "open" | "done" | "cancelled";

interface TaskFilters {
  mine: boolean;
  areaId: string;
  status: StatusFilter;
}

const STATUS_FILTER: Record<StatusFilter, string> = {
  open: `(${OPEN_STATUSES.join(",")})`,
  done: "(done)",
  cancelled: "(cancelled)",
};

// Tasks page (task 1.2): every task of the team, internal ones included,
// filtered by owner, area and status, with a quick create and the areas.
export const TenaxTaskList = () => {
  const translate = useTranslate();
  const isMobile = useIsMobile();
  const { identity } = useGetIdentity();
  const [filters, setFilters] = useStore<TaskFilters>("tenax.tasks.filters", {
    mine: true,
    areaId: "",
    status: "open",
  });
  const [isCreateOpen, setCreateOpen] = useState(false);
  const [isAreasOpen, setAreasOpen] = useState(false);
  const [isTemplatesOpen, setTemplatesOpen] = useState(false);
  const { data: areas = [] } = useTaskAreas();
  const { data: team = [] } = useTeam();

  const { data: tasks = [], isPending } = useGetList<TenaxTask>(
    "tasks",
    {
      pagination: { page: 1, perPage: 500 },
      sort: { field: "due_date", order: "ASC" },
      filter: {
        "status@in": STATUS_FILTER[filters.status],
        ...(filters.mine && identity ? { sales_id: identity.id } : {}),
        ...(filters.areaId ? { area_id: Number(filters.areaId) } : {}),
      },
    },
    { enabled: !filters.mine || !!identity },
  );

  const taskIds = tasks.map((task) => task.id);
  const { data: steps = [] } = useGetList<ChecklistItem>(
    "task_checklist_items",
    {
      pagination: { page: 1, perPage: 5000 },
      sort: { field: "id", order: "ASC" },
      filter: { "task_id@in": `(${taskIds.join(",")})` },
    },
    { enabled: taskIds.length > 0 },
  );

  const groups = useMemo(() => groupOpenTasks(tasks), [tasks]);

  const rowProps = {
    areaName: (id: number | null) => areas.find((area) => area.id === id)?.name,
    ownerName: (id: number | null) =>
      fullName(team.find((member) => member.id === id)),
    stepsOf: (id: number) => {
      const ofTask = steps.filter((step) => step.task_id === id);
      return {
        done: ofTask.filter((step) => step.done_at).length,
        total: ofTask.length,
      };
    },
  };

  const content = (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-2">
        <ToggleGroup
          type="single"
          variant="outline"
          value={filters.mine ? "mine" : "everyone"}
          onValueChange={(value) =>
            value && setFilters({ ...filters, mine: value === "mine" })
          }
        >
          <ToggleGroupItem value="mine">
            {translate("tenax.tasks.filters.mine")}
          </ToggleGroupItem>
          <ToggleGroupItem value="everyone">
            {translate("tenax.tasks.filters.everyone")}
          </ToggleGroupItem>
        </ToggleGroup>
        <select
          aria-label={translate("tenax.tasks.fields.area")}
          className={cn(nativeSelectClass, "w-auto")}
          value={filters.areaId}
          onChange={(event) =>
            setFilters({ ...filters, areaId: event.target.value })
          }
        >
          <option value="">{translate("tenax.tasks.filters.all_areas")}</option>
          {areas.map((area) => (
            <option key={area.id} value={area.id}>
              {area.name}
            </option>
          ))}
        </select>
        <select
          aria-label={translate("tenax.tasks.fields.status")}
          className={cn(nativeSelectClass, "w-auto")}
          value={filters.status}
          onChange={(event) =>
            setFilters({
              ...filters,
              status: event.target.value as StatusFilter,
            })
          }
        >
          {(["open", "done", "cancelled"] as const).map((status) => (
            <option key={status} value={status}>
              {translate(`tenax.tasks.filters.${status}`)}
            </option>
          ))}
        </select>
        <Button variant="ghost" onClick={() => setAreasOpen(true)}>
          {translate("tenax.tasks.areas.manage")}
        </Button>
        <Button variant="ghost" onClick={() => setTemplatesOpen(true)}>
          {translate("tenax.tasks.templates.manage")}
        </Button>
        <Button asChild variant="ghost">
          <Link to="/procedures">
            {translate("tenax.tasks.procedures.title")}
          </Link>
        </Button>
      </div>

      {!isPending && tasks.length === 0 && (
        <p className="text-muted-foreground text-sm">
          {translate("tenax.tasks.empty")}
        </p>
      )}

      {filters.status === "open" ? (
        (Object.keys(groups) as (keyof typeof groups)[]).map((key) =>
          groups[key].length > 0 ? (
            <TaskGroup
              key={key}
              title={translate(`tenax.tasks.groups.${key}`)}
              tasks={groups[key]}
              {...rowProps}
            />
          ) : null,
        )
      ) : (
        <TaskGroup
          title={translate("tenax.tasks.groups.closed")}
          tasks={tasks}
          {...rowProps}
        />
      )}

      <TaskQuickCreate
        open={isCreateOpen}
        onOpenChange={setCreateOpen}
        defaultAreaId={filters.areaId ? Number(filters.areaId) : undefined}
      />
      <TaskAreasDialog open={isAreasOpen} onOpenChange={setAreasOpen} />
      <TaskTemplatesDialog
        open={isTemplatesOpen}
        onOpenChange={setTemplatesOpen}
      />
    </div>
  );

  const newButton = (
    <Button onClick={() => setCreateOpen(true)}>
      {translate("tenax.tasks.new")}
    </Button>
  );

  if (isMobile) {
    return (
      <>
        <MobileHeader showRefresh>
          <h1 className="text-xl font-semibold">
            {translate("tenax.tasks.title")}
          </h1>
        </MobileHeader>
        <MobileContent>
          <div className="flex flex-col gap-4">
            {newButton}
            {content}
          </div>
        </MobileContent>
      </>
    );
  }

  return (
    <div className="mx-auto flex w-full max-w-4xl flex-col gap-4 py-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">
          {translate("tenax.tasks.title")}
        </h1>
        {newButton}
      </div>
      {content}
    </div>
  );
};
